import { CommonModule } from '@angular/common';
import { Component, EventEmitter, HostListener, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, inject } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { Subscription } from 'rxjs';
import { debounceTime, map } from 'rxjs/operators';
import { IconComponent } from '../icons/icon-reusable';
import { calenderIcon, exportIcon, ResetIcon, reassignIcon } from '../icons/icon';
import { ButtonComponent } from '../button-reusable/button-reusable';
import { TooltipDirective } from '../tooltip/tooltip.directive';
import { SelectComponent, SelectOption } from '../select/select';

export interface FilterOption {
  value: any;
  label: string;
  subtext?: string;
}

export interface FilterConfig {
  key: string;
  label: string;
  type: 'select' | 'text' | 'number' | 'date';
  options?: FilterOption[];
  placeholder?: string;
  searchable?: boolean;
}

export interface TabConfig {
  label: string;
  value: string;
  count?: number;
}

export interface TableFilterSectionConfig {
  showSearch?: boolean;
  searchPlaceholder?: string;
  showDateRange?: boolean;
  dateFormat?: string;
  filters?: FilterConfig[];
  showReset?: boolean;
  showExport?: boolean;
  showMarkAll?: boolean;
  showTabs?: boolean;
  tabs?: TabConfig[];
  showFilterChips?: boolean;
  initialFilters?: Record<string, any>;
  showReassign?: boolean;
  reassignTooltip?: string;
}

@Component({
  selector: 'app-table-filter-section',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, IconComponent, ButtonComponent, TooltipDirective, SelectComponent],
  templateUrl: './table-filter-section-reusable.html',
  styleUrl: './table-filter-section-reusable.css'
})
export class TableFilterSectionComponent implements OnInit, OnChanges, OnDestroy {
  private readonly formBuilder = inject(FormBuilder);

  @Input() config: TableFilterSectionConfig = {};
  @Input() activeTab = '';
  @Input() exportDisabled = false;
  @Input() exportPermission = true;
  @Input() reassignDisabled = false;
  @Input() containerClass = 'mb-5 rounded-md bg-white p-3 sm:p-3';

  @Output() filtersChange = new EventEmitter<Record<string, any>>();
  @Output() search = new EventEmitter<string>();
  @Output() reset = new EventEmitter<void>();
  @Output() export = new EventEmitter<Record<string, any>>();
  @Output() tabChange = new EventEmitter<string>();
  @Output() markAllRead = new EventEmitter<void>();
  @Output() reassign = new EventEmitter<void>();

  readonly filterForm: FormGroup = this.formBuilder.group({
    search: this.formBuilder.control(''),
    startDate: this.formBuilder.control(''),
    endDate: this.formBuilder.control('')
  });

  activeFilterChips: Array<{ key: string; label: string }> = [];
  readonly exportIcon = exportIcon;
  readonly resetIcon = ResetIcon;
  readonly calenderIcon = calenderIcon;
  readonly reassignIcon = reassignIcon;
  isDatePickerOpen = false;
  leftCalendarMonth = this.startOfMonth(new Date());
  rightCalendarMonth = this.addMonths(this.leftCalendarMonth, 1);
  readonly weekDays = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];
  readonly monthOptions = [
    { label: 'January', value: 0 },
    { label: 'February', value: 1 },
    { label: 'March', value: 2 },
    { label: 'April', value: 3 },
    { label: 'May', value: 4 },
    { label: 'June', value: 5 },
    { label: 'July', value: 6 },
    { label: 'August', value: 7 },
    { label: 'September', value: 8 },
    { label: 'October', value: 9 },
    { label: 'November', value: 10 },
    { label: 'December', value: 11 }
  ];
  readonly yearOptions = this.buildYearOptions();

  private readonly baseControlKeys = ['search', 'startDate', 'endDate'];
  private readonly subscriptions = new Subscription();
  private applyingState = false;
  private lastEmittedSearch = '';
  private lastEmittedFiltersPayload = this.serializeFiltersPayload(
    this.buildFilterPayload(this.filterForm.getRawValue())
  );

  ngOnInit(): void {
    this.syncFormWithConfig();
    this.setupListeners();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['config']) {
      this.syncFormWithConfig();
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  onReset(): void {
    const nextValue: Record<string, any> = {
      search: '',
      startDate: '',
      endDate: ''
    };

    for (const filter of this.config.filters ?? []) {
      nextValue[filter.key] = '';
    }

    this.applyFormValue(nextValue);
    this.closeDatePicker();
    this.reset.emit();
  }

  onExport(): void {
    if (this.exportDisabled) {
      return;
    }

    this.export.emit(this.filterForm.getRawValue());
  }

  onReassign(): void {
    this.reassign.emit();
  }

  getSelectOptions(filter: FilterConfig): SelectOption[] {
    return (filter.options || []).filter((option) => !this.isFilterLabelPlaceholder(filter, option));
  }

  private isFilterLabelPlaceholder(filter: FilterConfig, option: FilterOption): boolean {
    const hasEmptyValue = option.value === '' || option.value === null || option.value === undefined;
    const optionLabel = option.label.trim().toLowerCase();
    const filterLabel = filter.label.trim().toLowerCase();

    return hasEmptyValue && optionLabel === filterLabel;
  }

  toggleDatePicker(): void {
    this.isDatePickerOpen = !this.isDatePickerOpen;

    if (this.isDatePickerOpen) {
      const startDate = this.parseIsoDate(this.filterForm.get('startDate')?.value);
      const endDate = this.parseIsoDate(this.filterForm.get('endDate')?.value);
      if (endDate && startDate) {
        this.leftCalendarMonth = this.startOfMonth(startDate);
        this.rightCalendarMonth = this.startOfMonth(endDate);
        if (this.rightCalendarMonth <= this.leftCalendarMonth) {
          this.rightCalendarMonth = this.addMonths(this.leftCalendarMonth, 1);
        }
      } else {
        this.leftCalendarMonth = this.startOfMonth(startDate ?? new Date());
        this.rightCalendarMonth = this.addMonths(this.leftCalendarMonth, 1);
      }
    }
  }

  closeDatePicker(): void {
    this.isDatePickerOpen = false;
  }

  goToPreviousMonth(calendar: 'left' | 'right'): void {
    if (calendar === 'left') {
      this.leftCalendarMonth = this.addMonths(this.leftCalendarMonth, -1);
      return;
    }

    this.rightCalendarMonth = this.addMonths(this.rightCalendarMonth, -1);
  }

  goToNextMonth(calendar: 'left' | 'right'): void {
    if (calendar === 'left') {
      this.leftCalendarMonth = this.addMonths(this.leftCalendarMonth, 1);
      return;
    }

    this.rightCalendarMonth = this.addMonths(this.rightCalendarMonth, 1);
  }

  onCalendarMonthChange(calendar: 'left' | 'right', value: string): void {
    const month = Number(value);
    if (Number.isNaN(month) || month < 0 || month > 11) {
      return;
    }

    if (calendar === 'left') {
      this.leftCalendarMonth = new Date(this.leftCalendarMonth.getFullYear(), month, 1);
      return;
    }

    this.rightCalendarMonth = new Date(this.rightCalendarMonth.getFullYear(), month, 1);
  }

  onCalendarYearChange(calendar: 'left' | 'right', value: string): void {
    const year = Number(value);
    if (Number.isNaN(year)) {
      return;
    }

    if (calendar === 'left') {
      this.leftCalendarMonth = new Date(year, this.leftCalendarMonth.getMonth(), 1);
      return;
    }

    this.rightCalendarMonth = new Date(year, this.rightCalendarMonth.getMonth(), 1);
  }

  getMonthYearLabel(date: Date): string {
    return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  }

  getCalendarDays(monthDate: Date): Array<{ iso: string; day: number; inCurrentMonth: boolean }> {
    const start = this.startOfMonth(monthDate);
    const firstWeekday = start.getDay();
    const gridStart = new Date(start);
    gridStart.setDate(start.getDate() - firstWeekday);

    const days: Array<{ iso: string; day: number; inCurrentMonth: boolean }> = [];
    for (let i = 0; i < 42; i++) {
      const date = new Date(gridStart);
      date.setDate(gridStart.getDate() + i);

      days.push({
        iso: this.toIsoDate(date),
        day: date.getDate(),
        inCurrentMonth: date.getMonth() === monthDate.getMonth() && date.getFullYear() === monthDate.getFullYear()
      });
    }

    return days;
  }

  onDateSelect(isoDate: string): void {
    const startValue = String(this.filterForm.get('startDate')?.value ?? '');
    const endValue = String(this.filterForm.get('endDate')?.value ?? '');

    // No dates selected at all — start a new range
    if (!startValue && !endValue) {
      this.filterForm.patchValue({ startDate: isoDate, endDate: '' });
      return;
    }

    // Only start date exists and new date is before start — swap start
    if (startValue && !endValue && isoDate < startValue) {
      this.filterForm.patchValue({ startDate: isoDate, endDate: '' });
      return;
    }

    // Only start date exists and new date is after/equal start — complete range
    if (startValue && !endValue && isoDate >= startValue) {
      this.filterForm.patchValue({ endDate: isoDate });
      this.closeDatePicker();
      return;
    }

    // Both dates already exist — modifying an existing range
    if (startValue && endValue) {
      if (isoDate < startValue) {
        // Clicked before current start — restart range with new start
        this.filterForm.patchValue({ startDate: isoDate, endDate: '' });
      } else {
        // Clicked after current start — update end date
        this.filterForm.patchValue({ endDate: isoDate });
        this.closeDatePicker();
      }
      return;
    }

    // Fallback: set as start date
    this.filterForm.patchValue({ startDate: isoDate, endDate: '' });
  }

  clearDateRange(event?: Event): void {
    event?.stopPropagation();
    this.filterForm.patchValue({ startDate: '', endDate: '' });
  }

  getDateRangeDisplay(): string {
    const start = String(this.filterForm.get('startDate')?.value ?? '');
    const end = String(this.filterForm.get('endDate')?.value ?? '');

    if (!start && !end) {
      return 'dd-mm-yyyy - dd-mm-yyyy';
    }

    if (start && !end) {
      return `${this.formatDisplayDate(start)} - dd-mm-yyyy`;
    }

    if (!start && end) {
      return `dd-mm-yyyy - ${this.formatDisplayDate(end)}`;
    }

    return `${this.formatDisplayDate(start)} - ${this.formatDisplayDate(end)}`;
  }

  isSelectedDate(isoDate: string): boolean {
    const start = String(this.filterForm.get('startDate')?.value ?? '');
    const end = String(this.filterForm.get('endDate')?.value ?? '');
    return isoDate === start || isoDate === end;
  }

  isInSelectedRange(isoDate: string): boolean {
    const start = String(this.filterForm.get('startDate')?.value ?? '');
    const end = String(this.filterForm.get('endDate')?.value ?? '');

    if (!start || !end) {
      return false;
    }

    return isoDate > start && isoDate < end;
  }

  onTabChange(tabValue: string): void {
    this.activeTab = tabValue;
    this.tabChange.emit(tabValue);
  }

  removeFilter(key: string): void {
    if (key === 'dateRange') {
      this.filterForm.patchValue({ startDate: '', endDate: '' });
      return;
    }

    this.filterForm.get(key)?.setValue('');
  }

  getFilterChipsCount(): number {
    return this.activeFilterChips.length;
  }

  get resetDisabled(): boolean {
    const formValue = this.filterForm.getRawValue();

    if (String(formValue.search ?? '').trim() !== '') {
      return false;
    }

    if (String(formValue.startDate ?? '').trim() !== '' || String(formValue.endDate ?? '').trim() !== '') {
      return false;
    }

    for (const filter of this.config.filters ?? []) {
      if (String(formValue[filter.key] ?? '').trim() !== '') {
        return false;
      }
    }

    return true;
  }

  get resetIconClasses(): string {
    return 'text-inherit shrink-0';
  }

  get exportIconClasses(): string {
    return 'text-inherit shrink-0';
  }

  get reassignTooltip(): string {
    return this.config.reassignTooltip || '';
  }

  private syncFormWithConfig(): void {
    const currentValue = this.filterForm.getRawValue();
    const initialFilters = this.config.initialFilters ?? {};
    const allowedKeys = new Set<string>([...this.baseControlKeys, ...(this.config.filters ?? []).map((filter) => filter.key)]);

    for (const key of Object.keys(this.filterForm.controls)) {
      if (!allowedKeys.has(key)) {
        this.filterForm.removeControl(key);
      }
    }

    for (const filter of this.config.filters ?? []) {
      if (!this.filterForm.contains(filter.key)) {
        this.filterForm.addControl(filter.key, new FormControl(''));
      }
    }

    const initialValue: Record<string, any> = {
      search: this.resolveInitialFilterValue('search', currentValue, initialFilters),
      startDate: this.resolveInitialFilterValue('startDate', currentValue, initialFilters),
      endDate: this.resolveInitialFilterValue('endDate', currentValue, initialFilters)
    };

    for (const filter of this.config.filters ?? []) {
      initialValue[filter.key] = this.resolveInitialFilterValue(filter.key, currentValue, initialFilters);
    }

    this.applyFormValue(initialValue);
  }

  private resolveInitialFilterValue(
    key: string,
    currentValue: Record<string, any>,
    initialFilters: Record<string, any>
  ): any {
    // Prefer current value if it's already set (user may have already interacted with the filter)
    if (Object.prototype.hasOwnProperty.call(currentValue, key) && currentValue[key] !== '' && currentValue[key] !== null && currentValue[key] !== undefined) {
      return currentValue[key];
    }

    // Fall back to initial filters
    if (Object.prototype.hasOwnProperty.call(initialFilters, key)) {
      return initialFilters[key];
    }

    return '';
  }

  private setupListeners(): void {
    const searchControl = this.filterForm.get('search');

    if (searchControl) {
      this.subscriptions.add(
        searchControl.valueChanges.pipe(
          debounceTime(300),
          map((value) => String(value ?? ''))
        ).subscribe((value) => {
          if (this.applyingState) {
            return;
          }

          if (value === this.lastEmittedSearch) {
            this.updateFilterChips();
            return;
          }

          this.lastEmittedSearch = value;
          this.search.emit(value);
          this.updateFilterChips();
        })
      );
    }

    this.subscriptions.add(
      this.filterForm.valueChanges.pipe(
        debounceTime(150),
        map((value) => this.buildFilterPayload(value))
      ).subscribe((value) => {
        if (this.applyingState) {
          return;
        }

        const serializedValue = this.serializeFiltersPayload(value);
        if (serializedValue === this.lastEmittedFiltersPayload) {
          this.updateFilterChips();
          return;
        }

        this.lastEmittedFiltersPayload = serializedValue;
        this.filtersChange.emit(value);
        this.updateFilterChips();
      })
    );
  }

  private applyFormValue(value: Record<string, any>): void {
    this.applyingState = true;
    this.filterForm.patchValue(value, { emitEvent: false });
    const nextValue = this.filterForm.getRawValue();
    this.lastEmittedSearch = String(nextValue['search'] ?? '');
    this.lastEmittedFiltersPayload = this.serializeFiltersPayload(
      this.buildFilterPayload(nextValue)
    );
    this.updateFilterChips();
    this.applyingState = false;
  }

  private buildFilterPayload(value: Record<string, any>): Record<string, any> {
    const { search, ...rest } = value;
    return rest;
  }

  private serializeFiltersPayload(value: Record<string, any>): string {
    return JSON.stringify(value);
  }

  private updateFilterChips(): void {
    if (!this.config.showFilterChips) {
      this.activeFilterChips = [];
      return;
    }

    const formValue = this.filterForm.getRawValue();
    const chips: Array<{ key: string; label: string }> = [];

    if (formValue.search && String(formValue.search).trim() !== '') {
      chips.push({
        key: 'search',
        label: `Search: "${formValue.search}"`
      });
    }

    for (const filter of this.config.filters ?? []) {
      const value = formValue[filter.key];
      if (value === null || value === undefined || value === '') {
        continue;
      }

      const optionLabel = filter.options?.find((option) => option.value === value)?.label;
      chips.push({
        key: filter.key,
        label: `${filter.label}: ${optionLabel ?? value}`
      });
    }

    if (formValue.startDate || formValue.endDate) {
      const start = formValue.startDate || 'Any';
      const end = formValue.endDate || 'Any';
      chips.push({
        key: 'dateRange',
        label: `Date: ${start} to ${end}`
      });
    }

    this.activeFilterChips = chips;
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const target = event.target as HTMLElement | null;

    if (!target?.closest('[data-date-range-picker]')) {
      this.closeDatePicker();
    }
  }

  private startOfMonth(date: Date): Date {
    return new Date(date.getFullYear(), date.getMonth(), 1);
  }

  private addMonths(date: Date, months: number): Date {
    return new Date(date.getFullYear(), date.getMonth() + months, 1);
  }

  private toIsoDate(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  private parseIsoDate(value: unknown): Date | null {
    const dateValue = String(value ?? '');
    if (!/^\d{4}-\d{2}-\d{2}$/.test(dateValue)) {
      return null;
    }

    const parsed = new Date(`${dateValue}T00:00:00`);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }

  private formatDisplayDate(value: string): string {
    const parsed = this.parseIsoDate(value);
    if (!parsed) {
      return value;
    }

    const day = String(parsed.getDate()).padStart(2, '0');
    const month = String(parsed.getMonth() + 1).padStart(2, '0');
    const year = parsed.getFullYear();
    return `${day}-${month}-${year}`;
  }

  private buildYearOptions(): number[] {
    const currentYear = new Date().getFullYear();
    const years: number[] = [];
    for (let year = currentYear; year >= currentYear - 50; year--) {
      years.push(year);
    }

    return years;
  }
}
